/**
 * Turning browser reminders on and off.
 *
 * The browser half of `docs/push-notifications.md`. What arrives is handled in
 * `public/push-sw.js`; this is only the subscribing.
 */

/** Where the channel this browser registered is remembered between visits. */
const channelKey = 'cairn-push-channel';

export type PushState =
  | { supported: false }
  | { supported: true; subscribed: boolean; blocked: boolean };

export function pushSupported() {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * The VAPID public key, base64url as the server prints it, in the byte array
 * `PushManager.subscribe` insists on.
 */
function applicationServerKey(base64url: string) {
  const padded = base64url.replace(/-/g, '+').replace(/_/g, '/'),
    raw = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, '='));

  return Uint8Array.from(raw, character => character.charCodeAt(0));
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) {
    return { supported: false };
  }

  // `serviceWorker.ready` never settles until a worker is active, so asking it
  // here would hide the control on any page where registration has not finished
  // — including, confusingly, the first visit. `getRegistration` answers now.
  const registration = await navigator.serviceWorker.getRegistration();

  return {
    supported: true,
    subscribed: Boolean(await registration?.pushManager.getSubscription()),
    blocked: Notification.permission === 'denied',
  };
}

/**
 * Subscribes this browser and tells the server where to send.
 *
 * Returns the reason it could not, rather than throwing, because every one of
 * these is an ordinary answer: the owner declined the prompt, the deployment
 * has no VAPID key, the browser has notifications switched off at the system
 * level. None of them is an error worth a stack trace.
 */
export async function enablePush(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!pushSupported()) {
    return { ok: false, reason: 'This browser cannot receive reminders while it is closed.' };
  }

  const config = await fetch('/api/push/config').then(response => response.json());

  if (!config.publicKey) {
    return {
      ok: false,
      reason: 'This deployment has no push key yet. Run npm run push-keys on the server.',
    };
  }

  if ((await Notification.requestPermission()) !== 'granted') {
    return { ok: false, reason: 'Notifications are not allowed for this site.' };
  }

  if (!(await navigator.serviceWorker.getRegistration())) {
    return {
      ok: false,
      reason: 'Cairn’s service worker has not finished installing. Reload and try again.',
    };
  }

  const registration = await navigator.serviceWorker.ready;

  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      // Web Push allows a subscription without a payload key; Cairn always
      // sends one, and a reminder with no title is no reminder.
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(config.publicKey),
    }));

  const response = await fetch('/api/push/register', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platform: 'web', subscription: subscription.toJSON() }),
  });

  if (!response.ok) {
    return { ok: false, reason: 'The server refused this subscription.' };
  }

  localStorage.setItem(channelKey, ((await response.json()) as { id: string }).id);

  return { ok: true };
}

/**
 * Unsubscribes, and asks the server to forget this browser.
 *
 * The browser half happens first and unconditionally. If telling the server
 * fails, nothing is left broken: it will send once more, the push service will
 * answer `410 Gone` for a subscription that no longer exists, and the sender
 * retires the channel on its own.
 */
export async function disablePush() {
  if (!pushSupported()) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();

  await (await registration?.pushManager.getSubscription())?.unsubscribe();

  const id = localStorage.getItem(channelKey);

  localStorage.removeItem(channelKey);

  if (id) {
    await fetch('/api/push/forget', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id }),
    }).catch(() => {});
  }
}
